import type { AfterState } from "../../lib/meetings-view";
import { cn } from "../ui/cn";

/**
 * What a past meeting has left behind, as a small mark.
 *
 * Shapes as well as colours, because status is never colour alone here: a
 * tick for done, a page for notes only, an envelope for a follow-up only, and
 * a hollow red ring for nothing yet -- the one that should catch the eye.
 */
export function AfterIcon({ state, className }: { state: AfterState; className?: string }) {
  if (!state) return null;

  const label = AFTER_LABELS[state];

  return (
    <span title={label} aria-label={label} role="img" className={cn("inline-flex", className)}>
      {state === "done" ? (
        <svg viewBox="0 0 16 16" className="size-3 text-emerald-600 dark:text-emerald-400" aria-hidden>
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path d="M4.5 8.2 7 10.5l4.5-5" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : state === "notes" ? (
        <svg viewBox="0 0 16 16" className="size-3 text-amber-600 dark:text-amber-400" aria-hidden>
          <rect x="3" y="1.5" width="10" height="13" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      ) : state === "followed-up" ? (
        <svg viewBox="0 0 16 16" className="size-3 text-amber-600 dark:text-amber-400" aria-hidden>
          <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="m2 4.5 6 4.5 6-4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" className="size-3 text-red-600 dark:text-red-400" aria-hidden>
          <circle cx="8" cy="8" r="5.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
        </svg>
      )}
    </span>
  );
}

export const AFTER_LABELS: Record<Exclude<AfterState, null>, string> = {
  done: "Notes written and follow-up sent",
  notes: "Notes written, no follow-up yet",
  "followed-up": "Follow-up sent, no notes yet",
  nothing: "No notes or follow-up yet",
};
